import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

export const GPTSelector = ({
  selectedGptId,
  onSelectGPT,
  onCloseMobile,
  customGpts = [],
  apiUrl
}) => {
  // Separate default GPT from other GPTs
  const { defaultGpt, otherGpts } = useMemo(() => {
    const defaultGpt = customGpts.find(gpt => gpt.is_default);
    const otherGpts = customGpts
      .filter(gpt => !gpt.is_default)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { defaultGpt, otherGpts };
  }, [customGpts]);

  // Show a search lens (opens a centered dialog) when there are many assistants
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const showSearch = customGpts.length > 10;
  const trimmed = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    const sorted = [...customGpts].sort((a, b) => {
      if (!!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return trimmed
      ? sorted.filter(gpt => gpt.name?.toLowerCase().includes(trimmed))
      : sorted;
  }, [customGpts, trimmed]);

  // Render a GPT avatar: favicon if present, else gradient avatar (default vs custom)
  const renderGptAvatar = (gpt, size = 24, iconSize = 14) => {
    if (gpt.favicon_url) {
      return (
        <Avatar
          src={`${apiUrl}${gpt.favicon_url}?t=${Date.now()}`}
          alt={gpt.name}
          sx={{ width: size, height: size }}
        />
      );
    }
    const isDefault = gpt.is_default;
    return (
      <Avatar sx={{
        width: size,
        height: size,
        background: isDefault
          ? 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)'
          : 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
      }}>
        {isDefault
          ? <ChatBubbleOutlineIcon sx={{ fontSize: iconSize, color: 'white' }} />
          : <BoltIcon sx={{ fontSize: iconSize, color: 'white' }} />}
      </Avatar>
    );
  };

  const handleSelectGPT = (gptId) => {
    onSelectGPT(gptId);
    setSearch('');
    setSearchOpen(false);
    // Close mobile sidebar after selection
    if (window.innerWidth <= 768 && onCloseMobile) {
      onCloseMobile();
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 1.5,
        pt: 1.5,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider',
        maxHeight: '40%', // Limit GPT section to 40% of sidebar
        minHeight: 100, // Ensure at least some space for GPTs
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 1.5, flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Assistants
        </Typography>
        {showSearch && (
          <Tooltip title="Search assistants">
            <IconButton
              size="small"
              onClick={() => setSearchOpen(true)}
              sx={{ color: 'text.secondary', p: 0.5 }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* GPT List - scrollable */}
      <List
        disablePadding
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0,
          '& .MuiListItemButton-root': { borderRadius: 1, mb: 0.25 },
        }}
      >
        {/* Default GPT (company's General GPT or fallback) */}
        {defaultGpt ? (
          <ListItemButton
            selected={selectedGptId === defaultGpt.id || selectedGptId === '' || !selectedGptId}
            onClick={() => handleSelectGPT(defaultGpt.id)}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {defaultGpt.favicon_url ? (
                <Avatar
                  src={`${apiUrl}${defaultGpt.favicon_url}?t=${Date.now()}`}
                  alt={defaultGpt.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                  }}
                >
                  <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary={defaultGpt.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        ) : (
          // Fallback for companies without a default GPT (pre-migration)
          <ListItemButton
            selected={selectedGptId === '' || !selectedGptId}
            onClick={() => handleSelectGPT('')}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary="General"
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        )}

        {/* Custom GPTs (excluding the default) */}
        {otherGpts.map((gpt) => (
          <ListItemButton
            key={gpt.id}
            selected={selectedGptId === gpt.id}
            onClick={() => handleSelectGPT(gpt.id)}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {gpt.favicon_url ? (
                <Avatar
                  src={`${apiUrl}${gpt.favicon_url}?t=${Date.now()}`}
                  alt={gpt.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
                  }}
                >
                  <BoltIcon sx={{ fontSize: 14, color: 'white' }} />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary={gpt.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Search dialog - centered, matches the chat-search style */}
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearch(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
            mt: 8,
          },
        }}
      >
        {/* Search input */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search assistants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => { setSearchOpen(false); setSearch(''); }}
                    size="small"
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: 6 },
            }}
          />
        </Box>

        {/* Results - fixed height so the dialog never shrinks/grows */}
        <Box sx={{ height: 360, overflowY: 'auto', pb: 2 }}>
          {trimmed && searchResults.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 8 }}>
              No assistants found
            </Typography>
          ) : (
            <List disablePadding>
              {searchResults.map((gpt) => (
                <ListItemButton
                  key={gpt.id}
                  selected={selectedGptId === gpt.id}
                  onClick={() => handleSelectGPT(gpt.id)}
                  sx={{ px: 2, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {renderGptAvatar(gpt, 28, 16)}
                  </ListItemIcon>
                  <ListItemText
                    primary={gpt.name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};
